/**
 * Utility functions for image manipulation using dotstudioPRO API
 *
 * The dotstudioPRO API allows image URLs to be resized by appending /width/height
 * to the URL path, and supports optional query params for blur and webp format.
 *
 * @see https://api.myspotlight.tv/#section/Image-Manipulation
 */
import { API_CONFIG } from '../config/AppConfig';
import { cacheService } from '../services/CacheService';

/**
 * Normalizes a CDN base URL so it ends in exactly one `/images` segment.
 * The API-supplied base may or may not already include it (the vendor DSP
 * returns `https://bkfcdspmedia.cachefly.net/images/`, our bridge returns
 * `{canonicalUrl}images/`) — this keeps bare-id image URLs from doubling up
 * (`//images/images/`) or missing the segment entirely.
 *
 * @param {string} base - CDN base URL, with or without a trailing `/images`
 * @returns {string} base URL ending in `/images`, no trailing slash
 */
export const normalizeImageCdnBase = base => {
  const trimmed = (base || '').replace(/\/+$/, '');
  return trimmed.endsWith('/images') ? trimmed : `${trimmed}/images`;
};

/**
 * Single neutral fallback image, inlined as a data URI so it can never itself
 * 404 or hit a dead host (unlike the old via.placeholder.com / local
 * /placeholder-image.jpg, which was never committed). Used everywhere an
 * image is missing or fails to load, regardless of aspect ratio — it's
 * rendered with `object-fit: cover` at each call site.
 */
export const FALLBACK_IMAGE_SRC =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 225">' +
      '<rect width="400" height="225" fill="#1a1a1a"/>' +
      '<path d="M150 145l35-40 30 28 45-55 40 50v22a4 4 0 01-4 4H154a4 4 0 01-4-4z" fill="#333"/>' +
      '<circle cx="170" cy="90" r="14" fill="#333"/>' +
      '</svg>',
  );

let warnedNoImageBase = false;

/**
 * The base to hang a bare asset id off, or null when it is not known.
 *
 * The only source is the API (`/init/config` -> `image_cdn_url`, primed at
 * boot by SpotlightService.primeImageCdnUrl). There is no build-time env var
 * and no hardcoded host: if the API never supplied one, this returns null and
 * the caller renders its placeholder. Returning a relative `/images/<id>` URL
 * instead would resolve against the web origin and 404 — that was the bug.
 *
 * @returns {string|null} base ending in `/images` (no trailing slash), or null
 */
export const getImageCdnBase = () => {
  const base = cacheService.getImageCdnUrl();
  if (!base) {
    if (!warnedNoImageBase) {
      warnedNoImageBase = true;
      console.error(
        'ImageUtils: no image CDN base available (/init/config supplied no ' +
          'image_cdn_url); bare-asset-id images will render the placeholder',
      );
    }
    return null;
  }
  return normalizeImageCdnBase(base);
};

const hostOf = url => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

/**
 * Checks if a URL is from a dotstudioPRO image domain, or our own API/CDN host
 * (the bridge serves sizable images at the same `/{id}/{w}/{h}` path).
 *
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL supports the sizing/webp query grammar
 */
export const isDotstudioProImage = url => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Known dotstudioPRO image domains (dying with the DSP contract) plus our own
  // API/CDN hosts, which the bridge now serves images from.
  const dotstudioDomains = [
    'images.dotstudiopro.com',
    'dprodspmedia.cachefly.net',
    'cdn.dotstudiopro.com',
    'dprodspimages.s3.amazonaws.com',
    hostOf(API_CONFIG.BASE_URL),
  ].filter(Boolean);

  return dotstudioDomains.some(domain => url.includes(domain));
};

/**
 * Optimizes an image URL from dotstudioPRO API by adding size parameters
 *
 * @param {string} imageUrl - The original image URL from the API
 * @param {Object} options - Configuration options
 * @param {number} options.width - Desired image width
 * @param {number} options.height - Desired image height
 * @param {number} options.blur - Optional blur amount (1-100)
 * @param {boolean} options.webp - Whether to request WebP format
 * @param {boolean} options.highDpi - Whether to double size for high DPI displays
 * @returns {string} Optimized image URL
 */
export const optimizeImageUrl = (imageUrl, options = {}) => {
  // Default options - increased for wider container
  const defaults = {
    width: 400,
    height: 225,
    blur: null,
    webp: true,
    highDpi: window.devicePixelRatio > 1.5,
  };

  // Merge defaults with provided options
  const config = { ...defaults, ...options };

  // If not a dotstudioPRO image URL, return original
  if (!imageUrl || !isDotstudioProImage(imageUrl)) {
    return imageUrl;
  }

  try {
    // Parse the URL to handle both path and query parameters correctly
    const url = new URL(imageUrl);
    const baseUrl = url.origin + url.pathname;

    // Calculate dimensions, accounting for high DPI if needed
    const width = config.highDpi ? config.width * 2 : config.width;
    const height = config.highDpi ? config.height * 2 : config.height;

    // Create the size-optimized URL
    let optimizedUrl = `${baseUrl}/${width}/${height}`;

    // Add query parameters if needed
    const params = new URLSearchParams();
    if (config.blur && config.blur >= 1 && config.blur <= 100) {
      params.append('blur', config.blur);
    }
    if (config.webp) {
      params.append('webp', '1');
    }

    // Add any existing query parameters from the original URL
    for (const [key, value] of new URLSearchParams(url.search)) {
      if (key !== 'blur' && key !== 'webp') {
        params.append(key, value);
      }
    }

    // Append query parameters if any exist
    const queryString = params.toString();
    if (queryString) {
      optimizedUrl += `?${queryString}`;
    }

    return optimizedUrl;
  } catch (error) {
    console.error('Error optimizing image URL:', error);
    return imageUrl; // Return original URL if there's an error
  }
};

/**
 * Predefined image size presets for different UI contexts
 */
export const ImageSizePresets = {
  // Channel card thumbnails - increased for wider container
  CHANNEL_CARD_THUMBNAIL: {
    width: 400,
    height: 225,
    webp: true,
  },

  // Channel card thumbnails for mobile
  CHANNEL_CARD_THUMBNAIL_MOBILE: {
    width: 300,
    height: 169,
    webp: true,
  },

  // Channel detail hero image
  CHANNEL_DETAIL_HERO: {
    width: 1920,
    height: 1080,
    webp: true,
  },

  // Category thumbnail - increased for wider container
  CATEGORY_THUMBNAIL: {
    width: 500,
    height: 281,
    webp: true,
  },

  // Video thumbnail - increased for wider container
  VIDEO_THUMBNAIL: {
    width: 400,
    height: 225,
    webp: true,
  },

  // Profile avatar
  PROFILE_AVATAR: {
    width: 100,
    height: 100,
    webp: true,
  },

  // Episode thumbnail for Netflix navigation
  EPISODE_THUMBNAIL: {
    width: 320,
    height: 180,
    webp: true,
  },

  // Hover modal thumbnail
  HOVER_MODAL_THUMBNAIL: {
    width: 541,
    height: 304,
    webp: true,
  },

  // Related content thumbnail
  RELATED_CONTENT_THUMBNAIL: {
    width: 320,
    height: 180,
    webp: true,
  },
};

/**
 * Gets the appropriate image size preset based on screen width
 *
 * @param {string} presetType - The type of preset to get
 * @returns {Object} The appropriate size preset
 */
export const getResponsiveImagePreset = presetType => {
  const isMobile = window.innerWidth < 768;

  switch (presetType) {
    case 'CHANNEL_CARD':
      return isMobile
        ? ImageSizePresets.CHANNEL_CARD_THUMBNAIL_MOBILE
        : ImageSizePresets.CHANNEL_CARD_THUMBNAIL;
    case 'CHANNEL_HERO':
      return ImageSizePresets.CHANNEL_DETAIL_HERO;
    case 'CATEGORY':
      return ImageSizePresets.CATEGORY_THUMBNAIL;
    case 'VIDEO':
      return ImageSizePresets.VIDEO_THUMBNAIL;
    case 'AVATAR':
      return ImageSizePresets.PROFILE_AVATAR;
    case 'EPISODE_THUMBNAIL':
      return ImageSizePresets.EPISODE_THUMBNAIL;
    case 'HOVER_MODAL':
      return ImageSizePresets.HOVER_MODAL_THUMBNAIL;
    case 'RELATED_CONTENT':
      return ImageSizePresets.RELATED_CONTENT_THUMBNAIL;
    default:
      return ImageSizePresets.CHANNEL_CARD_THUMBNAIL;
  }
};

export default {
  optimizeImageUrl,
  isDotstudioProImage,
  getImageCdnBase,
  ImageSizePresets,
  getResponsiveImagePreset,
};
